import { stackOrderNone, stackOffsetNone } from 'd3';
import { getRegionSortingOrder } from "@atlas-int/viz";
import { TradeFlow, TradeDirection, LocationDetailLevel } from "@atlas-int/data/types";
import { computeTotalSumByTradeFlow, determineReferenceLocation, MetadataMergeType } from "@atlas-int/data";
import { mergeDataWithTopLevelParent } from '../../../Utils';
import { MetadataFetchStatus, filterTradeData } from "@atlas-int/data";
import transformLocations from '../transformLocations';
import { sortLocationsForRibbons } from '../Utils';
import { buildStackData, TransformedTradeDataItem } from './buildStackData';
import { locationMembersInclude } from "@atlas-int/data/types";
import type { RawSeriesData, TradeValueScaledByYAxisOption, YearRangeForProductClass } from '../types';
import type { LocationMetadataPayload, DeflatorDatum, RegionMetadata, CountryMetadata } from "@atlas-int/data/types";
import type { TradeDataItem as SharedTradeDataItem } from "@atlas-int/data/types";

/** A trade data item with dynamic properties from merges and transforms */
interface TradeDataItem extends SharedTradeDataItem {
  year: number;
  partnerId?: string;
  topLevelParent?: string;
}

/** A single location metadata item */
interface LocationMetadatumItem {
  countryId?: string | number;
  groupId?: string;
  parentId?: string | null;
  nameShortEn?: string | null;
  groupName?: string;
  iso3Code?: string | null;
  members?: (string | number)[];
}

export interface TransformMarketsViewParams {
  data: TradeDataItem[];
  exporter: string | undefined;
  importer: string | undefined;
  locationMetadataStatus: string;
  locationMetadata: LocationMetadataPayload | undefined;
  deflatorsMetadataStatus: string;
  deflatorsMetadata: DeflatorDatum[] | undefined;
  locationLevel: LocationDetailLevel;
  hiddenCategories: string[];
  yearMin: number;
  yearMax: number;
  yearRangeForProductClass: YearRangeForProductClass;
  tradeFlow: TradeFlow;
  tradeDirection: TradeDirection;
  tradeDirectionSelector: string;
  scaledTradeValueSelector: TradeValueScaledByYAxisOption;
  stackOrderFunction: typeof stackOrderNone;
  stackOffsetFunction: typeof stackOffsetNone;
  setYAxisDomain: (domain: { min: number; max: number }) => void;
}

export interface TransformViewResult {
  seriesData: RawSeriesData[] | undefined;
  totalSum: number | undefined;
  noDataToDisplay: boolean;
  stackKeysSet: Set<string> | undefined;
}

export const transformMarketsView = ({
  data,
  exporter,
  importer,
  locationMetadataStatus,
  locationMetadata,
  deflatorsMetadataStatus,
  deflatorsMetadata,
  locationLevel,
  hiddenCategories,
  yearMin,
  yearMax,
  yearRangeForProductClass,
  tradeFlow,
  tradeDirection,
  tradeDirectionSelector,
  scaledTradeValueSelector,
  stackOrderFunction,
  stackOffsetFunction,
  setYAxisDomain,
}: TransformMarketsViewParams): TransformViewResult => {
  let seriesData: RawSeriesData[] | undefined = undefined;
  let totalSum: number | undefined = undefined;
  let stackKeysSet: Set<string> | undefined = undefined;
  let noDataToDisplay: boolean = false;

  const locations: LocationMetadataPayload | undefined =
    locationMetadataStatus === MetadataFetchStatus.Success ? locationMetadata : undefined;

  const deflators: DeflatorDatum[] | undefined =
    deflatorsMetadataStatus === MetadataFetchStatus.Success ? deflatorsMetadata : undefined;

  if (locations) {
    const { regions, subregions, countries } = locations;

    let dataWithTopLevelParentId = mergeDataWithTopLevelParent({
      data,
      mergeType: MetadataMergeType.Region,
      regionsMetadata: regions,
    });

    let dataSortedByCategory;
    let sortingOrder: string[] = getRegionSortingOrder();
    if (sortingOrder) {
      dataSortedByCategory = [...dataWithTopLevelParentId].sort(
        (locationA: TradeDataItem, locationB: TradeDataItem) =>
          sortLocationsForRibbons({ locationA, locationB, sortingOrder }),
      );
    } else {
      dataSortedByCategory = dataWithTopLevelParentId;
    }

    // Step 2: Use shared determineReferenceLocation
    const referenceLocation = determineReferenceLocation(exporter, importer);

    // Step 3: Use shared filterTradeData
    let filteredData = filterTradeData({
      data: dataSortedByCategory,
      hiddenCategories,
    });

    let filteredDataWithinYearLimits = filteredData.filter(
      (d) => d.year !== undefined && d.year >= yearMin! && d.year <= yearMax!,
    );

    totalSum =
      filteredDataWithinYearLimits.length > 0
        ? computeTotalSumByTradeFlow({
            data: filteredData.filter((d) => d.year === yearMax),
            tradeFlow,
            tradeDirection,
          })
        : 0;

    const transformedData = transformLocations({
      untransformedData: filteredData as Parameters<typeof transformLocations>[0]["untransformedData"],
      location: referenceLocation,
      locationDetailLevel: locationLevel,
      regions: regions as unknown as RegionMetadata[],
      subregions: subregions as unknown as RegionMetadata[],
      countries: countries as unknown as CountryMetadata[],
      deflators: deflators as unknown as DeflatorDatum[],
      yearRangeForProductClass,
      tradeFlow,
    }) as unknown as TransformedTradeDataItem[] | undefined;

    if (transformedData) {
      // Step 8: Use shared buildStackData
      const { series, stackKeysSet: keys } = buildStackData({
        transformedData,
        yearMin: yearMin!,
        yearMax: yearMax!,
        entityIdAccessor: (d) => d.partnerId,
        scaledTradeValueSelector,
        tradeDirectionSelector,
        stackOrderFunction,
        stackOffsetFunction,
        setYAxisDomain,
      });
      stackKeysSet = keys;

      // Build location lookup for O(1) metadata enrichment
      const allLocations = [...regions, ...subregions, ...countries];
      const locationByCountryId = new Map<string | number, LocationMetadatumItem>();
      const locationByGroupId = new Map<string, LocationMetadatumItem>();
      allLocations.forEach((loc: LocationMetadatumItem) => {
        if (loc.countryId != null) locationByCountryId.set(loc.countryId, loc);
        if (loc.groupId != null) locationByGroupId.set(loc.groupId, loc);
      });

      const seriesWithMetadata: RawSeriesData[] = series.map((d) => {
        const partnerId = d.key;
        let topLevelParent: string | undefined;
        let nameEn: string | undefined;
        let countryIso3Code: string | undefined = undefined;
        let productCode: string | undefined = undefined;
        let topLevelParentName: string | undefined = undefined;

        const findMatchingMetadataForPartner =
          locationByCountryId.get(partnerId) || locationByGroupId.get(partnerId);

        if (findMatchingMetadataForPartner) {
          const { nameShortEn, groupName, iso3Code } = findMatchingMetadataForPartner;
          nameEn = nameShortEn ?? groupName ?? undefined;
          if (iso3Code) countryIso3Code = iso3Code;
        }

        if (locationLevel === 'region') {
          topLevelParent = partnerId;
        } else if (locationLevel === 'subregion') {
          const findMatchingMetadata = subregions.find(
            (subregion: LocationMetadatumItem) => subregion.groupId === partnerId,
          );
          if (findMatchingMetadata) {
            topLevelParent = findMatchingMetadata.parentId ?? undefined;
          }
        } else {
          const findMatchingMetadata = regions.find(
            (region: LocationMetadatumItem) =>
              locationMembersInclude(region.members, partnerId),
          );
          if (findMatchingMetadata) {
            topLevelParent = findMatchingMetadata.groupId;
          }
        }

        return {
          key: partnerId,
          topLevelParent,
          nameEn,
          countryIso3Code,
          productCode,
          topLevelParentName,
          seriesData: [...d],
        };
      });

      seriesData = seriesWithMetadata;
    } else {
      noDataToDisplay = true;
    }
  }

  return { seriesData, totalSum, noDataToDisplay, stackKeysSet };
};
